import { useEffect, useRef, useState } from 'react';
import api from '../../services/api';
import { ActiveJob, JobStatus } from './types';

const POLL_INTERVAL_MS = 4000;

// Celery reports PENDING both for a queued task and for an unknown task id, so
// a job that no worker ever picks up would otherwise be polled forever. The
// trigger endpoints pre-check for live workers, which makes that rare, but this
// cap guarantees the poller terminates.
const POLL_TIMEOUT_MS = 10 * 60 * 1000;

interface UseJobStatusResult {
    status: JobStatus | null;
    timedOut: boolean;
}

/**
 * Poll a re-trigger job until it finishes.
 *
 * Uses a recursive setTimeout rather than setInterval so a slow response can
 * never overlap with the next request.
 */
export function useJobStatus(
    sessionId: number | null,
    job: ActiveJob | null,
    onFinished?: (status: JobStatus) => void,
): UseJobStatusResult {
    const [status, setStatus] = useState<JobStatus | null>(null);
    const [timedOut, setTimedOut] = useState(false);

    // Held in a ref so a new callback identity on each parent render does not
    // restart the poll.
    const onFinishedRef = useRef(onFinished);
    onFinishedRef.current = onFinished;

    const taskId = job?.taskId ?? null;
    const upstreamTaskId = job?.upstreamTaskId ?? null;

    useEffect(() => {
        if (!sessionId || !taskId) {
            setStatus(null);
            setTimedOut(false);
            return;
        }

        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;
        const startedAt = Date.now();

        setStatus(null);
        setTimedOut(false);

        const poll = async () => {
            if (cancelled) return;

            if (Date.now() - startedAt > POLL_TIMEOUT_MS) {
                setTimedOut(true);
                return;
            }

            try {
                const params = new URLSearchParams({ task_id: taskId });
                if (upstreamTaskId) {
                    params.append('upstream_task_id', upstreamTaskId);
                }
                const response = await api.get(
                    `/super-admin/interview-sessions/${sessionId}/job_status/?${params}`,
                );
                if (cancelled) return;

                const next: JobStatus = response.data;
                setStatus(next);

                if (next.ready) {
                    onFinishedRef.current?.(next);
                    return;
                }
            } catch (error: any) {
                // Keep polling through transient failures — the job itself is
                // still running. A permanent failure ends at POLL_TIMEOUT_MS.
                console.error('Error polling job status:', error);
                if (cancelled) return;
            }

            timer = setTimeout(poll, POLL_INTERVAL_MS);
        };

        poll();

        return () => {
            cancelled = true;
            if (timer) clearTimeout(timer);
        };
    }, [sessionId, taskId, upstreamTaskId]);

    return { status, timedOut };
}
